import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CommonFunctionsService, DatastoreService } from '../../services';
import { isOurCeremonyFeatureEnabled } from '../../services/guards/feature-toggle.guard';

/**
 * Resources that support the extend journey, read from config at
 * `myCeremony.extendCeremony.showExtendButtonForResourceIds`. The Extend button
 * on our-ceremony reads the same list, so the guard and the button stay in
 * lock-step. Returns [] (fail closed — extend disabled) if config is missing or
 * malformed.
 */
export function getExtendAllowedResourceIds(dataStore: DatastoreService): number[] {
  try {
    const config = dataStore.config ? JSON.parse(dataStore.config) : {};
    const ids = config?.myCeremony?.extendCeremony?.showExtendButtonForResourceIds;
    if (Array.isArray(ids)) {
      return ids.map((id: any) => Number(id)).filter((n) => !Number.isNaN(n));
    }
    console.warn('extendCeremony.showExtendButtonForResourceIds missing or not an array in config');
  } catch (err) {
    console.error('Failed to read extend resource ids from config', err);
  }
  return [];
}

/**
 * Resolves a booking's resource id. The bookings-list shape can carry it either
 * as `_links.resource.href` (…/resource/{id}) or a flat `resource_id`, so we
 * try the link first and fall back. Returns null when neither is present.
 */
export function getBookingResourceId(booking: any): number | null {
  const fromLink = booking?._links?.resource?.href?.split('/').pop();
  const raw = fromLink ?? booking?.resource_id;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

/**
 * Single source of truth for "can this booking be extended": the can_extend
 * toggle is on, a booking is loaded, it hasn't already been extended
 * (settings.extended_at stamp or an extension extra-item link), and its
 * resource is on the extend allowlist. Drives the our-ceremony Extend button
 * and the cross-journey link on the reschedule before-you-start page, so a
 * visible link can never disagree with the extend route guards and bounce
 * the user to our-ceremony.
 */
export function isExtendAvailable(dataStore: DatastoreService): boolean {
  if (!isOurCeremonyFeatureEnabled(dataStore, 'can_extend')) {
    return false;
  }
  const booking = dataStore.booking;
  if (!booking) {
    return false;
  }
  if (booking?.settings?.extended_at || dataStore.bookingHasExtensionItem) {
    return false;
  }
  const resourceId = getBookingResourceId(booking);
  return resourceId != null && getExtendAllowedResourceIds(dataStore).includes(resourceId);
}

/**
 * Blocks the extend routes when the booking's resource isn't extend-eligible.
 * Mirrors the canExtend check in our-ceremony.component, so users can't bypass
 * the hidden Extend button via URL bar / bookmark / back.
 *
 * If there's no booking in the datastore yet, defer to extendRequiresBookingGuard
 * (which redirects to our-ceremony on a missing booking).
 */
export const extendAllowedGuard: CanActivateFn = () => {
  const router = inject(Router);
  const dataStore = inject(DatastoreService);
  const commonFunctionsService = inject(CommonFunctionsService);

  const booking = dataStore.booking;
  if (!booking) {
    return true;
  }

  const resourceId = getBookingResourceId(booking);
  const allowedResourceIds = getExtendAllowedResourceIds(dataStore);
  if (resourceId != null && allowedResourceIds.includes(resourceId)) {
    return true;
  }

  router.navigate([
    `${commonFunctionsService.getCompanyName()}/${commonFunctionsService.getCompanyId()}/our-ceremony`,
  ]);
  return false;
};
